// Contextual help rail, informational only - no functional controls here.
// Fills the leftover side space at wide viewports instead of stretching
// the reading card.
//
// Built as <details>/<summary> rather than a plain <aside>: summary is
// natively focusable/operable with Enter or Space, and it collapses behind
// a toggle at narrow viewports for free. style.css pins it as a fixed
// sidebar column only above the wide breakpoint; it's the same element
// either way.
import { el } from "./dom";
import type { TabId } from "../main";

interface HelpCopy {
  title: string;
  paragraphs: string[];
}

const HELP_COPY: Record<TabId, HelpCopy> = {
  overview: {
    title: "Reading this like API docs",
    paragraphs: [
      "Every other page pairs integration instructions (SDK, CLI, and HTTP snippets) with a live, working form for that exact capability - the form isn't a mockup, it runs the real crypto in your browser right now.",
      "Pick whichever integration path fits: the SDK for another web app, the CLI for scripts/backends, or the Service API page for the optional HTTP storage/lookup relay.",
    ],
  },
  "service-api": {
    title: "What this relay does and doesn't do",
    paragraphs: [
      "Every request on this page runs for real against the service configured for this build - nothing here is simulated. It only ever stores or relays ciphertext and public keys; it never receives plaintext, a password, or a private key.",
      "Publishing a public key or uploading a container needs an API token - request one right here on this page, no sign-in required, or use a permanent admin-issued key for scripted/automated use.",
    ],
  },
  keygen: {
    title: "About your identity key",
    paragraphs: [
      "Your passphrase protects the private half of this keypair - it's what stands between anyone else and your ability to open messages sent to you. Nobody, including this app, can recover it if you forget it.",
      "This works whether or not you have an API token - a token is unrelated to this key and never sees it. You'd only need one later, if you want to upload an encrypted file for someone else to fetch.",
    ],
  },
  "encrypt-string": {
    title: "Encrypting a message",
    paragraphs: [
      "You need the recipient's public key here, not your own. A public key is safe to share freely - having it only lets someone seal a message to that person, never open one.",
      "The result is ciphertext you can paste anywhere - chat, email, a text file. Only whoever holds the matching private key can turn it back into plaintext.",
    ],
  },
  "decrypt-string": {
    title: "Decrypting a message",
    paragraphs: [
      "This uses whichever private key is currently unlocked in the Generate keypair tab - nothing is looked up automatically. If nothing's unlocked yet, generate or unlock a key there first.",
      "A message sealed to a different public key than the one you currently have unlocked will fail to open. That's expected - it means the ciphertext just isn't addressed to this key, not that something's broken.",
    ],
  },
  "encrypt-file": {
    title: "Multiple ways to unlock one file",
    paragraphs: [
      "Each \"way to unlock\" is an independent password that can open the whole file on its own - like cutting several distinct keys for the same lock, rather than handing everyone the same key.",
      "Or switch to \"A recipient's public key\" to seal it for one specific person instead - a one-time password is generated for you and sealed to their key, so you never handle it yourself. They'll need both the encrypted file and the small envelope this produces.",
      "Sealing the file happens entirely in your browser and never needs an API token. You'll only be asked for one if you click \"Upload to service\" afterward (requested automatically, no sign-in) - that's the one step that actually talks to the relay.",
    ],
  },
  "decrypt-file": {
    title: "If your password doesn't work",
    paragraphs: [
      "You only need one of the file's passwords - enter the one you were given. The app checks it against every way the file was sealed automatically; you don't need to know which one is \"yours\".",
      "Shared with your public key instead? Paste the envelope you were given in its own field - it's unsealed with whichever key is currently active, no password needed. \"View\" opens the result without saving a decrypted copy through this app.",
      "Decrypting a file you already have locally never needs an API token. Fetching one from the relay by its blob_id does - one is requested automatically for that step, no sign-in needed.",
    ],
  },
  security: {
    title: "Why the browser is different",
    paragraphs: [
      "A web page can't prove which exact code it's running the way an installed, hash-checked binary can - that's the core trade-off of using this in a browser tab instead of the CLI.",
      "This app narrows that gap as much as a browser allows: a strict content security policy, no third-party scripts, and all crypto isolated inside a dedicated Web Worker your main thread's extensions can't casually reach into.",
    ],
  },
  integration: {
    title: "Picking an integration path",
    paragraphs: [
      "Crypto and storage are two separate, independently optional pieces - you can use the SDK/CLI for the crypto and bring your own storage entirely, or lean on this relay for storage too. Neither requires the other.",
      "The \"share a file for a recipient\" flow (CLI or SDK) needs no interactive input on the sending side, so it's safe to call in a loop from a backend.",
    ],
  },
  sdk: {
    title: "What's in the package, and what isn't",
    paragraphs: [
      "The SDK is just the crypto primitives - no worker RPC client, no API token handling, no HTTP client. Those are specific to this app's own UI, not something another app installing the SDK needs.",
      "Runs anywhere Web Streams are available: a browser main thread, a Web Worker, or Node (with a polyfill on older versions).",
    ],
  },
};

export function renderHelpRail(tabId: TabId): HTMLElement {
  const copy = HELP_COPY[tabId];
  return el("details", { class: "help-rail", open: "", "aria-label": "Contextual help" }, [
    el("summary", {}, [copy.title]),
    el(
      "div",
      { class: "help-rail-body" },
      copy.paragraphs.map((p) => el("p", {}, [p])),
    ),
  ]);
}
